import { Icon, type IconName } from "@/components/ui";
import { isPlainClick, pathFor } from "@/lib/route";
import { type Theme, applyTheme, getAppliedTheme, nextTheme, persistTheme } from "@/lib/theme";
import { useIsMobile } from "@/lib/useViewport";
// Shell.tsx - app sidebar + top bar.
import { Fragment, useState } from "react";

const iconBtnStyle = (active?: boolean): React.CSSProperties => ({
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: "var(--radius-pill)",
  background: active ? "var(--accent)" : "var(--surface-inset)",
  border: 0,
  color: active ? "var(--text-on-accent)" : "var(--text-primary)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export type ScreenId =
  | "overview"
  | "items"
  | "schedule"
  | "issues"
  | "erp"
  | "mytasks"
  | "master"
  | "feedback"
  | "superadmin";

interface NavItem {
  id: ScreenId;
  /**
   * 사이드바 라벨. 264px 폭에서 「개발품 종합현황」은 잘리므로 축약한다.
   * 계획서 §3 원문 라벨은 화면 H1(`App.tsx` 의 `TITLES`)이 담보한다.
   */
  label: string;
  en: string;
  icon: IconName;
  /** 위에 1px 구분선을 둔다. 주 메뉴 4개와 보조 2개를 나눈다 (§3). */
  dividerBefore?: boolean;
}

/**
 * 계획서 §3 은 주 메뉴를 **4개**로 고정하고, §18 이 요구하는 `내 업무` ·
 * `마스터 / 설정` 은 그 아래 별도 묶음이다. 구분선이 그 경계다.
 * 슈퍼어드민은 메뉴 수 셈에서 제외한다 (`is_superadmin` 일 때만 보인다).
 */
export const NAV: NavItem[] = [
  { id: "overview", label: "종합현황", en: "Overview", icon: "home" },
  { id: "items", label: "개발품", en: "Dev Items", icon: "list" },
  { id: "schedule", label: "일정", en: "Schedule", icon: "calendar" },
  { id: "issues", label: "지연 · 이슈", en: "Issues", icon: "alert" },
  // 기존 ERP(soonsoft) 실사용 기능 이관분 - 개발품 주 메뉴 4개와 섞이지 않게 따로 묶는다.
  { id: "erp", label: "ERP 업무", en: "ERP", icon: "grid", dividerBefore: true },
  { id: "mytasks", label: "내 업무", en: "My Tasks", icon: "check", dividerBefore: true },
  { id: "master", label: "마스터", en: "Master", icon: "db" },
  // 슈퍼어드민 전용 - AppSidebar 의 showSuperadmin 플래그가 없으면 숨겨진다.
  { id: "superadmin", label: "슈퍼어드민", en: "Superadmin", icon: "eye" },
];

// NAV/탑바에서 숨기는 화면. 코드·라우팅은 유지되며 집합에서 id 를 빼면 즉시
// 복구된다(단일 소스). feedback 은 탑바 버튼이라 App.tsx 에서 가드.
export const HIDDEN_SCREENS = new Set<ScreenId>(["feedback"]);

export interface ShellUser {
  name: string;
  role: string;
  dept: string;
  initial: string;
}

const RailNavItem = ({
  item,
  active,
  open,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  /** 레일이 펼쳐진 상태인가. 라벨의 표시 여부를 정한다. */
  open: boolean;
  onClick: () => void;
}) => {
  const [h, setH] = useState(false);
  const [pressed, setPressed] = useState(false);
  // 링크다 - 탭마다 주소가 있어 새 탭으로 열기·주소 복사가 된다. 보통 클릭만 앱이 처리한다.
  return (
    <a
      href={pathFor({ screen: item.id })}
      onClick={(e) => {
        if (!isPlainClick(e)) return;
        e.preventDefault();
        onClick();
      }}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => {
        setH(false);
        setPressed(false);
      }}
      /* apple-design 1: 피드백은 pointer-down 에 붙는다. click(=up) 을
         기다리면 죽은 느낌이 난다. */
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      aria-current={active ? "page" : undefined}
      title={open ? undefined : item.label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        height: 40,
        padding: "0 10px",
        flexShrink: 0,
        border: 0,
        borderRadius: "var(--radius-control)",
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
        textDecoration: "none",
        boxSizing: "border-box",
        color: active || h ? "var(--rail-fg)" : "var(--rail-fg-dim)",
        background: active ? "var(--rail-active)" : h ? "var(--rail-hover)" : "transparent",
        fontWeight: active ? 600 : 500,
        fontSize: 13.5,
        transform: pressed ? "scale(0.97)" : "scale(1)",
        transition:
          "color var(--dur-fast) var(--ease-standard)," +
          "background var(--dur-fast) var(--ease-standard)," +
          "transform var(--dur-press) var(--ease-out)",
        userSelect: "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* 활성 표시는 색이 아니라 왼쪽 규칙선이다. 크롬이 무채색이라
          색으로 활성을 말할 수 없고, 말해서도 안 된다 - 색은 상태 전용. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: 8,
          bottom: 8,
          width: 2,
          borderRadius: "0 2px 2px 0",
          background: "var(--rail-fg)",
          opacity: active ? 1 : 0,
          transition: "opacity var(--dur-fast) var(--ease-standard)",
        }}
      />
      <span style={{ display: "inline-flex", flexShrink: 0, width: 20, justifyContent: "center" }}>
        <Icon name={item.icon} size={19} stroke={active ? 2.3 : 1.9} color="currentColor" />
      </span>
      {/* 라벨은 펼쳤을 때만 읽힌다. width 가 아니라 opacity 로 지우면
          레이아웃이 흔들리지 않는다 (transform/opacity 만 애니메이션). */}
      <span
        style={{
          flex: 1,
          whiteSpace: "nowrap",
          opacity: open ? 1 : 0,
          transition: "opacity var(--dur-base) var(--ease-standard)",
          pointerEvents: "none",
        }}
      >
        {item.label}
      </span>
      <span
        aria-hidden="true"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--rail-fg-dim)",
          whiteSpace: "nowrap",
          opacity: open ? 0.75 : 0,
          transition: "opacity var(--dur-base) var(--ease-standard)",
        }}
      >
        {item.en}
      </span>
    </a>
  );
};

/**
 * AppSidebar - 접히는 아이콘 레일.
 *
 * 이전 판은 264px 고정 폭에 흰 카드 두 장을 얹은 형태였고, 형제 제품
 * jw-cms 와 같은 파일에서 온 같은 실루엣이었다. 두 가지를 바꿨다.
 *
 * 1. 재질 - 레일은 라이트/다크 양쪽에서 어둡다. apple-design 12 의
 *    "무거운 재질이 구조 영역을 가른다". 테마가 아니라 역할이 정한다.
 * 2. 폭 - 60px 로 접고 hover/포커스에서 224px 로 펼친다. 계획서 4-2 의
 *    12열 표에 가로 200px 이 돌아온다. 열 위치는 그대로라
 *    DESIGN_VARIANCE 3(예측 가능한 좌표)을 깨지 않는다.
 *
 * 펼침은 `overlay` 로 뜬다 - 레이아웃 폭을 밀면 표가 매번 리플로우되고
 * 그게 훑는 눈을 방해한다. 모바일 드로어는 항상 펼친 상태로 들어온다.
 */
export const AppSidebar = ({
  active,
  onNavigate,
  user,
  onProfileClick,
  onCreateItem,
  showSuperadmin,
  style,
  alwaysOpen = false,
}: {
  active: ScreenId;
  onNavigate: (id: ScreenId) => void;
  user: ShellUser;
  onProfileClick: () => void;
  /** 빠른 작업 [개발품 등록] - 개발품 화면의 등록 폼을 바로 연다. */
  onCreateItem?: () => void;
  showSuperadmin?: boolean;
  style?: React.CSSProperties;
  /** 모바일 드로어처럼 접힘이 의미 없는 자리에서 항상 펼친다. */
  alwaysOpen?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const open = alwaysOpen || hovered || focused;
  const items = NAV.filter(
    (it) => !HIDDEN_SCREENS.has(it.id) && (it.id !== "superadmin" || showSuperadmin)
  );

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
      style={{
        width: alwaysOpen ? "var(--rail-w-open)" : "var(--rail-w)",
        flexShrink: 0,
        position: "relative",
        zIndex: "var(--z-rail)",
        ...style,
      }}
    >
      <nav
        aria-label="주요 메뉴"
        style={{
          // 접힌 레일은 fixed - absolute 면 페이지 스크롤에 같이 밀려 올라간다.
          position: alwaysOpen ? "relative" : "fixed",
          inset: alwaysOpen ? undefined : "0 auto 0 0",
          height: alwaysOpen ? "100%" : "100dvh",
          width: open ? "var(--rail-w-open)" : "var(--rail-w)",
          background: "var(--rail-bg)",
          borderRight: "1px solid var(--rail-edge)",
          padding: "10px 8px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "hidden",
          boxShadow: open && !alwaysOpen ? "var(--shadow-overlay)" : "none",
          transition:
            "width var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            height: 44,
            padding: "0 10px",
            flexShrink: 0,
            marginBottom: 6,
          }}
        >
          <img
            src="/logo.png"
            alt="한부금속"
            width={22}
            height={22}
            style={{ borderRadius: "var(--radius-glyph)", display: "block", flexShrink: 0 }}
          />
          <div
            style={{
              lineHeight: 1.15,
              whiteSpace: "nowrap",
              opacity: open ? 1 : 0,
              transition: "opacity var(--dur-base) var(--ease-standard)",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--rail-fg)" }}>한부금속</div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                color: "var(--rail-fg-dim)",
                letterSpacing: "0.18em",
                fontWeight: 500,
              }}
            >
              PDS
            </div>
          </div>
        </div>

        {items.map((it) => (
          <Fragment key={it.id}>
            {it.dividerBefore && (
              <hr
                style={{
                  border: 0,
                  borderTop: "1px solid var(--rail-edge)",
                  margin: "6px 10px",
                  flexShrink: 0,
                }}
              />
            )}
            <RailNavItem
              item={it}
              active={active === it.id}
              open={open}
              onClick={() => onNavigate(it.id)}
            />
          </Fragment>
        ))}

        <div style={{ flex: 1, minHeight: 8 }} />

        <button
          type="button"
          onClick={onCreateItem ?? (() => onNavigate("items"))}
          title="개발품 등록"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            height: 38,
            padding: "0 10px",
            flexShrink: 0,
            border: "1px solid var(--rail-edge)",
            borderRadius: "var(--radius-control)",
            background: "var(--rail-hover)",
            color: "var(--rail-fg)",
            font: "inherit",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            textAlign: "left",
            marginBottom: 6,
          }}
        >
          <span
            style={{ display: "inline-flex", width: 20, justifyContent: "center", flexShrink: 0 }}
          >
            <Icon name="plus" size={17} />
          </span>
          <span
            style={{
              whiteSpace: "nowrap",
              opacity: open ? 1 : 0,
              transition: "opacity var(--dur-base) var(--ease-standard)",
              pointerEvents: "none",
            }}
          >
            개발품 등록
          </span>
        </button>

        <button
          type="button"
          onClick={onProfileClick}
          title="개인 정보 설정"
          aria-label="개인 정보 설정"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            height: 44,
            padding: "0 8px",
            flexShrink: 0,
            borderTop: "1px solid var(--rail-edge)",
            borderLeft: 0,
            borderRight: 0,
            borderBottom: 0,
            borderRadius: 0,
            background: "transparent",
            width: "100%",
            textAlign: "left",
            font: "inherit",
            color: "var(--rail-fg)",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: "var(--radius-pill)",
              background: "var(--rail-fg)",
              color: "var(--rail-bg)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {user.initial}
          </span>
          <span
            style={{
              lineHeight: 1.25,
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              opacity: open ? 1 : 0,
              transition: "opacity var(--dur-base) var(--ease-standard)",
            }}
          >
            <span
              style={{
                display: "block",
                fontSize: 12.5,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user.name}
            </span>
            <span style={{ display: "block", fontSize: 10.5, color: "var(--rail-fg-dim)" }}>
              {user.role} · {user.dept}
            </span>
          </span>
        </button>
      </nav>
    </aside>
  );
};

export const AppTopBar = ({
  title,
  query,
  setQuery,
  onLogout,
  crumbs,
  action,
  onFeedback,
  feedbackActive,
  onMenu,
}: {
  title: string;
  query: string;
  setQuery: (v: string) => void;
  onLogout: () => void;
  crumbs?: string;
  action?: { icon: IconName; label: string; onClick: () => void };
  onFeedback?: () => void;
  feedbackActive?: boolean;
  // Mobile only: opens the nav drawer. When set, a hamburger renders at far left.
  onMenu?: () => void;
}) => {
  const [f, setF] = useState(false);
  const [theme, setTheme] = useState<Theme>(getAppliedTheme);
  const mobile = useIsMobile();
  const toggleTheme = () => {
    const t = nextTheme(theme);
    setTheme(t);
    applyTheme(t);
    persistTheme(t);
  };
  return (
    // 재질은 .jw-chrome 이 소유한다. 인라인 backdrop-filter 로 두면
    // prefers-reduced-transparency 블록이 무효화할 대상이 없어서 투명도
    // 감소 설정에서도 탑바가 계속 블러된다.
    <header
      className="jw-chrome sticky top-0 z-chrome"
      style={{
        minHeight: 64,
        padding: mobile ? "10px 14px" : "0 28px",
        display: "flex",
        alignItems: "center",
        gap: mobile ? 10 : 18,
        flexWrap: mobile ? "wrap" : "nowrap",
      }}
    >
      {onMenu && (
        <button
          type="button"
          onClick={onMenu}
          title="메뉴"
          aria-label="메뉴"
          style={iconBtnStyle()}
        >
          <Icon name="list" size={20} />
        </button>
      )}
      <div style={{ minWidth: 0, flex: mobile ? 1 : undefined }}>
        {crumbs && !mobile && (
          <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginBottom: 2 }}>
            {crumbs}
          </div>
        )}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontWeight: 800,
            fontSize: mobile ? 17 : 19,
            letterSpacing: "-0.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
      </div>
      {!mobile && <div style={{ flex: 1 }} />}
      <div
        style={{
          position: "relative",
          width: mobile ? "100%" : 300,
          order: mobile ? 9 : undefined,
          flexBasis: mobile ? "100%" : undefined,
          height: 40,
          background: "var(--surface-inset)",
          borderRadius: "var(--radius-pill)",
          padding: "0 18px 0 42px",
          display: "flex",
          alignItems: "center",
          boxShadow: f
            ? "var(--accent) 0px 0px 0px 2px inset"
            : "var(--border-strong) 0px 0px 0px 1px inset",
          transition: "box-shadow 120ms",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 15,
            color: f ? "var(--text-primary)" : "var(--text-secondary)",
            display: "flex",
          }}
        >
          <Icon name="search" size={17} />
        </span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setF(true)}
          onBlur={() => setF(false)}
          aria-label="개발품 · 품번 · 고객사 검색"
          placeholder="개발품 · 품번 · 고객사 검색"
          style={{
            background: "transparent",
            border: 0,
            outline: 0,
            color: "var(--text-primary)",
            fontSize: 13.5,
            width: "100%",
            fontFamily: "inherit",
          }}
        />
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          title={action.label}
          aria-label={action.label}
          style={iconBtnStyle(true)}
        >
          <Icon name={action.icon} size={18} />
        </button>
      )}
      {onFeedback && (
        <button
          type="button"
          onClick={onFeedback}
          title="개선 · 이슈"
          aria-label="개선 · 이슈"
          style={iconBtnStyle(feedbackActive)}
        >
          <Icon name="message" size={18} />
        </button>
      )}
      <button
        type="button"
        onClick={toggleTheme}
        title={theme === "dark" ? "라이트 모드로 전환" : "다크 모드로 전환"}
        aria-label={theme === "dark" ? "라이트 모드로 전환" : "다크 모드로 전환"}
        style={iconBtnStyle()}
      >
        <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
      </button>
      <button
        type="button"
        onClick={onLogout}
        title="로그아웃"
        style={{ ...iconBtnStyle(), color: "var(--text-secondary)" }}
      >
        <Icon name="logout" size={17} />
      </button>
    </header>
  );
};
